import { useState, useEffect } from 'react';
import { useApiUrl } from '@refinedev/core';
import { toast } from '@/hooks/useToast';
import { X, CreditCard, AlertCircle, Loader2 } from 'lucide-react';
import { adminFetch, AdminApiError } from '@/providers/admin-api';

const VALID_TIERS = ['free', 'gold', 'platinum'] as const;

export interface SetTierModalProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  currentTier: string;
  onTierSet?: (effectiveTier: string) => void;
}

export function SetTierModal({
  open,
  onClose,
  userId,
  currentTier,
  onTierSet,
}: SetTierModalProps) {
  const apiUrl = useApiUrl();
  const [tier, setTier] = useState<string>(currentTier);
  const [reason, setReason] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Reset state whenever the modal opens
  useEffect(() => {
    if (open) {
      setTier(currentTier);
      setReason('');
      setError(null);
      setSubmitting(false);
    }
  }, [open, currentTier]);

  const reasonValid = reason.trim().length >= 3;
  const canSubmit = (VALID_TIERS as readonly string[]).includes(tier) && reasonValid && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await adminFetch(
        `${apiUrl}/v1/admin/users/${userId}/subscription-tier`,
        { method: 'PATCH', body: JSON.stringify({ tier, reason: reason.trim() }) },
      );
      const { tier: effectiveTier } = (await res.json()) as { tier: string };
      toast({
        title: 'Subscription tier updated',
        description:
          effectiveTier !== tier
            ? `Requested ${tier}; effective tier is ${effectiveTier} (user holds a higher claim).`
            : `Set to ${effectiveTier}.`,
        variant: effectiveTier !== tier ? 'warning' : 'success',
      });
      onTierSet?.(effectiveTier);
      onClose();
    } catch (e: unknown) {
      if (e instanceof AdminApiError) {
        if (e.status === 404) {
          setError('User not found.');
        } else if (e.status === 403) {
          setError('Self-grant of a paid tier is blocked in production.');
        } else {
          setError(e.message);
        }
      } else {
        setError('Failed to update tier.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-lg bg-card shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b p-5">
          <h2 className="flex items-center gap-2 text-lg font-semibold">
            <CreditCard className="h-5 w-5" />
            Set Subscription Tier
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-muted-foreground hover:text-foreground disabled:opacity-50"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="space-y-4 p-5">
          {/* Tier selector */}
          <div>
            <label className="mb-1 block text-sm font-medium">
              Tier <span className="text-destructive">*</span>
            </label>
            <select
              value={tier}
              onChange={(e) => setTier(e.target.value)}
              disabled={submitting}
              className="w-full rounded-lg border bg-background px-3 py-2 text-sm disabled:opacity-50"
            >
              <option value="free">Free</option>
              <option value="gold">Gold</option>
              <option value="platinum">Platinum</option>
            </select>
          </div>

          {/* Reason */}
          <div>
            <label className="mb-1 block text-sm font-medium">
              Reason <span className="text-destructive">*</span>
            </label>
            <textarea
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={submitting}
              placeholder="e.g. tester grant / support comp ticket #1234 / founding member upgrade"
              className="w-full rounded-lg border bg-background px-3 py-2 text-sm disabled:opacity-50"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Required (≥ 3 chars). Persisted to admin_audit_log.
            </p>
          </div>

          {/* Effective-tier note */}
          <p className="text-xs text-muted-foreground">
            The effective tier may exceed your selection if the user already holds a higher
            coupon-granted claim — the saved value is what the server returns.
          </p>

          {/* Inline error */}
          {error && (
            <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-destructive" />
              <div className="text-destructive">{error}</div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t p-5">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-lg px-4 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            {submitting ? 'Saving…' : 'Set Tier'}
          </button>
        </div>
      </div>
    </div>
  );
}
